import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { IconDone } from 'icons/components/IconDone'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { replacePlaceholders } from 'labels/lib/replacePlaceholders'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { Domains } from 'monitoring/constants/domains'
import { useDynatrace } from 'monitoring/contexts/DynatraceProvider'
import { sendError } from 'monitoring/sendError/server/sendError'
import {
	DEFAULT_SUBSCRIPTION_BRAND,
	NewsletterAnalytics,
	NewsletterOrigin,
} from 'newsletter/constants'
import { newsletterSubscriptionService } from 'newsletter/services/newsletterSubscriptionService'
import type { NewsletterSubscriptionServiceParams } from 'newsletter/types/service'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext/useNotificationsContext'
import { useCallback, useState } from 'react'
import { useSession } from 'session/src/hooks/useSession'
import { type BrandId, OutletBrand, ShopBrand } from 'types/brands'
import { Channel, Platform } from 'types/masterData'
import { GenericEvents } from '../../constants/analytics'
import { backInStockSubscriptionService } from '../../services/backInStockSubscriptionService'
import type { BackInStockSubscriptionServiceParams } from '../../types'
import type { FormValues } from '../../types/form'
import { useBackInStockModalContext } from '../useBackInStockModalContext/useBackInStockModalContext'

const defaultBrand: Record<string, BrandId> = {
	[Channel.Shop]: ShopBrand.she,
	[Channel.Outlet]: OutletBrand.outlet,
}

export const useBackInStock = (isNewsletterEnabled = false) => {
	const {
		brand,
		channel,
		country: { countryISO, languageISO },
		isApp,
		device,
	} = useMasterData()
	const { logged, externalId } = useSession()
	const { t } = useLabels()
	const { addNotification } = useNotificationsContext()
	const { backInStockModalProps, closeProductSubscription } =
		useBackInStockModalContext()
	const { reportCustomError } = useDynatrace()

	const [subscribeError, setSubscribeError] = useState<boolean>(false)
	const [subscribeSuccess, setSubscribeSuccess] = useState<boolean>(false)

	const subscribeProduct = useCallback(
		async ({ email, newsletter }: FormValues) => {
			if (!backInStockModalProps) {
				throw sendError({
					domain: Domains.Product,
					message:
						'[ProductInfo] Back in stock props for subscribe are undefined',
				})
			}

			const {
				productSizeId,
				productColorId,
				productId,
				productName,
				feedbackDelay,
			} = backInStockModalProps

			const params: BackInStockSubscriptionServiceParams = {
				productId,
				productName,
				email,
				device,
				newsletter,
				channelId: channel,
				brand: brand ?? defaultBrand[channel],
				languageIso: languageISO,
				sizeId: productSizeId,
				colorId: productColorId,
				countryIso: countryISO,
				platform: Platform.Web,
				...(externalId ? { externalId } : {}),
			}

			try {
				await backInStockSubscriptionService(params)
				setSubscribeSuccess(true)
				sendGenericEvent(GenericEvents.NOTIFY_ME_SEND_SUCCEED, {
					sku: `${productId}-${productColorId}-${productSizeId}`,
				})
			} catch {
				backInStockModalProps.onError?.()
				return setSubscribeError(true)
			}

			if (isNewsletterEnabled && newsletter) {
				const payload: NewsletterSubscriptionServiceParams = {
					email,
					subscriptionOrigin: NewsletterOrigin.BACK_IN_STOCK,
					countryIso: countryISO,
					languageIso: languageISO,
					navigation: {
						channel,
						storeId: (brand ?? DEFAULT_SUBSCRIPTION_BRAND).toLocaleLowerCase(),
						userAgent: {
							appView: isApp,
							deviceType: device,
						},
					},
				}

				try {
					await newsletterSubscriptionService(payload)

					sendGenericEvent(NewsletterAnalytics.NEWSLETTER_SUBSCRIBE, {
						method: NewsletterAnalytics.NEWSLETTER_NATIVE,
						newsletter_subscription: 1,
						location: 'notify_me',
					})
				} catch (error) {
					reportCustomError({
						key: 'BACK IN STOCK: newsletter subscription error',
						message: error as string,
						hint: JSON.stringify({
							logged,
							channel,
							brand,
							countryISO,
							languageISO,
						}),
					})
				}
			}

			setTimeout(() => {
				addNotification({
					id: 'backInStock.feedbackSuccess',
					title: replacePlaceholders(
						t('notifyMe.snackbar.feedback.noLogin.title'),
						{
							1: email,
						}
					),
					icon: <IconDone color='content-inverse' />,
					dataTestId: 'backInStock.feedback',
				})

				closeProductSubscription()
			}, feedbackDelay)

			backInStockModalProps.onSuccess?.()

			return undefined
		},
		[isNewsletterEnabled]
	)

	return {
		subscribeError,
		subscribeSuccess,
		subscribeProduct,
	}
}
